import React from 'react';
import { ISearchElementStyle } from '../searchPageConfig';
import { elementStyleFrom } from '../styleControls';

/**
 * Live sample of the header's account control, for the Account & Sign In panel.
 *
 * Painted on the storefront's OWN header colour rather than a neutral swatch: this control ships on
 * the header bar, and a gold-on-white pill that looks fine against a form field can be invisible
 * against a maroon bar. The same reasoning the shared style frame gives for its dark variant.
 *
 * Both states are shown because one set of parts drives both — signed out reads "Guest / Sign in",
 * signed in shows the shopper's name — and a merchant styling one needs to see what it did to the
 * other. The markup mirrors the runtime's (Components/Auth/LoginPopover + NavBar's account trigger):
 * a flex pill holding a round avatar and one or two text lines, with the same defaults, so what shows
 * here is what the storefront renders.
 */
interface IAccountPersonaExampleProps {
    /** headerConfig.account — the pill. */
    trigger?: ISearchElementStyle;
    /** headerConfig.accountName — the primary line. */
    name?: ISearchElementStyle;
    /** headerConfig.accountSubtext — the "Sign in" line. */
    subtext?: ISearchElementStyle;
    /** headerConfig.accountAvatar — the round avatar. */
    avatar?: ISearchElementStyle;
    /** The storefront's header bar colour, so the sample sits on what it will ship on. */
    headerColor?: string;
    /** The header's text colour, which the pill inherits until it sets its own. */
    headerTextColor?: string;
}

const Pill: React.FC<{
    props: IAccountPersonaExampleProps;
    primary: string;
    secondary?: string;
    initials?: string;
}> = ({ props, primary, secondary, initials }) => (
    // Defaults duplicated from LoginPopover so an unconfigured preview matches an unconfigured store.
    <div style={{
        display: 'flex', alignItems: 'center', cursor: 'default',
        backgroundColor: 'lightgrey', borderRadius: 15, padding: '0.3em 1em',
        ...elementStyleFrom(props.trigger),
    }}>
        <div style={{
            width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: '#e1dfdd', color: '#323130', fontSize: 12, fontWeight: 600,
            ...elementStyleFrom(props.avatar),
        }}>
            {initials ?? '\u{1F464}'}
        </div>
        <div style={{ marginLeft: 8, lineHeight: 1.2, textAlign: 'left' }}>
            <div style={{ fontSize: 13, ...elementStyleFrom(props.name) }}>{primary}</div>
            {!!secondary && <div style={{ fontSize: 11, ...elementStyleFrom(props.subtext) }}>{secondary}</div>}
        </div>
    </div>
);

const AccountPersonaExample: React.FC<IAccountPersonaExampleProps> = (props) => (
    <div>
        <div style={{
            display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap',
            padding: '12px 14px', borderRadius: 4,
            backgroundColor: props.headerColor || '#ffffff',
            color: props.headerTextColor || '#1a1a1a',
            // A configured header colour can be near-white, which would leave the sample floating with
            // no edge; the hairline keeps the swatch readable whatever colour it takes.
            border: '1px solid #edebe9',
        }}>
            <Pill props={props} primary='Guest' secondary='Sign in' />
            <Pill props={props} primary='Ada Lovelace' initials='AL' />
        </div>
        <div style={{ fontSize: 11, color: '#605e5c', marginTop: 4 }}>
            Signed out and signed in, on this storefront&apos;s header colour.
        </div>
    </div>
);

export default AccountPersonaExample;
